/**
 * ComparisonColumn Component - Displays a single site in comparison view
 * Requirements: 4.2, 4.3, 4.4
 */

import { SiteDetail } from '../services/api';

interface ComparisonColumnProps {
  siteId: string;
  siteName: string;
  siteDetail: SiteDetail | null;
  isLoading: boolean;
  isMostOpportunity: boolean;
  isMostCostEffective: boolean;
}

function ComparisonColumn({
  siteName,
  siteDetail,
  isLoading,
  isMostOpportunity,
  isMostCostEffective,
}: ComparisonColumnProps) {
  if (isLoading) {
    return (
      <div style={styles.column}>
        <div style={styles.header}>
          <h3 style={styles.siteName}>{siteName}</h3>
        </div>
        <div style={styles.loading}>Loading...</div>
      </div>
    );
  }

  if (!siteDetail) {
    return (
      <div style={styles.column}>
        <div style={styles.header}>
          <h3 style={styles.siteName}>{siteName}</h3>
        </div>
        <div style={styles.error}>Failed to load site data</div>
      </div>
    );
  }

  // Calculate metrics
  const totalPlannedCases = siteDetail.capacity.reduce((sum, row) => sum + row.plannedCases, 0);
  const totalLoadCapacity = siteDetail.capacity.reduce(
    (sum, row) => sum + (row.loadCapacity || row.plannedCases),
    0
  );
  const totalOpenCapacity = siteDetail.capacity.reduce(
    (sum, row) => sum + (row.openCapacity || 0),
    0
  );
  const utilization = totalLoadCapacity > 0 ? totalPlannedCases / totalLoadCapacity : 0;
  const costPerCase =
    totalPlannedCases > 0 ? siteDetail.site.costParameters.fixedCost / totalPlannedCases : 0;

  const formatNumber = (num: number): string => num.toLocaleString();
  const formatCurrency = (num: number): string => `$${num.toFixed(2)}`;
  const formatPercent = (num: number): string => `${(num * 100).toFixed(1)}%`;

  return (
    <div style={styles.column}>
      {/* Header with badges */}
      <div style={styles.header}>
        <h3 style={styles.siteName}>{siteName}</h3>
        <div style={styles.badges}>
          {isMostOpportunity && (
            <span style={{ ...styles.badge, backgroundColor: '#28a745' }}>Most Opportunity</span>
          )}
          {isMostCostEffective && (
            <span style={{ ...styles.badge, backgroundColor: '#007bff' }}>Most Cost-Effective</span>
          )}
        </div>
      </div>

      {/* Key Metrics */}
      <div style={styles.metricsSection}>
        <div style={styles.metricCard}>
          <span style={styles.metricLabel}>Utilization</span>
          <span
            style={{
              ...styles.metricValue,
              color: utilization < 0.7 ? '#28a745' : utilization > 0.95 ? '#dc3545' : '#333',
            }}
          >
            {formatPercent(utilization)}
          </span>
        </div>

        <div style={styles.metricCard}>
          <span style={styles.metricLabel}>Open Capacity</span>
          <span style={styles.metricValue}>{formatNumber(totalOpenCapacity)}</span>
          <span style={styles.metricUnit}>cases</span>
        </div>

        <div style={styles.metricCard}>
          <span style={styles.metricLabel}>Cost Per Case</span>
          <span style={styles.metricValue}>{formatCurrency(costPerCase)}</span>
        </div>
      </div>

      {/* Utilization Bar */}
      <div style={styles.utilizationSection}>
        <div style={styles.utilizationLabel}>Capacity Utilization</div>
        <div style={styles.utilizationBarContainer}>
          <div
            style={{
              ...styles.utilizationBar,
              width: `${Math.min(utilization * 100, 100)}%`,
              backgroundColor:
                utilization < 0.7 ? '#28a745' : utilization > 0.95 ? '#dc3545' : '#ffc107',
            }}
          />
        </div>
        <div style={styles.utilizationDetails}>
          <span>{formatNumber(totalPlannedCases)} planned</span>
          <span>{formatNumber(totalLoadCapacity)} capacity</span>
        </div>
      </div>

      {/* Cost Summary */}
      <div style={styles.costSection}>
        <div style={styles.sectionTitle}>Cost Parameters</div>
        <div style={styles.costRow}>
          <span>Fixed Cost</span>
          <span>${(siteDetail.site.costParameters.fixedCost / 1000000).toFixed(1)}MM</span>
        </div>
        <div style={styles.costRow}>
          <span>Total Budget</span>
          <span>${(siteDetail.site.costParameters.totalBudget / 1000000).toFixed(1)}MM</span>
        </div>
        <div style={styles.costRow}>
          <span>Fixed %</span>
          <span>{(siteDetail.site.costParameters.fixedCostPercentage * 100).toFixed(0)}%</span>
        </div>
      </div>

      {/* Format Breakdown */}
      <div style={styles.formatSection}>
        <div style={styles.sectionTitle}>Format Breakdown</div>
        <div style={styles.formatList}>
          {siteDetail.capacity.slice(0, 5).map((row) => (
            <div key={row.format} style={styles.formatRow}>
              <span style={styles.formatName}>{row.format}</span>
              <span
                style={{
                  ...styles.formatUtilization,
                  color: row.utilization >= 1 ? '#dc3545' : '#666',
                }}
              >
                {formatPercent(row.utilization)}
              </span>
            </div>
          ))}
          {siteDetail.capacity.length > 5 && (
            <div style={styles.moreFormats}>+{siteDetail.capacity.length - 5} more formats</div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  column: {
    backgroundColor: 'white',
    borderRadius: '8px',
    border: '1px solid #dee2e6',
    overflow: 'hidden',
  },
  header: {
    padding: '1rem',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #dee2e6',
  },
  siteName: {
    margin: 0,
    fontSize: '1.1rem',
    color: '#333',
  },
  badges: {
    display: 'flex',
    gap: '0.5rem',
    marginTop: '0.5rem',
    flexWrap: 'wrap',
  },
  badge: {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    color: 'white',
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  loading: {
    padding: '2rem',
    textAlign: 'center',
    color: '#666',
  },
  error: {
    padding: '2rem',
    textAlign: 'center',
    color: '#dc3545',
  },
  metricsSection: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '0.5rem',
    padding: '1rem',
    borderBottom: '1px solid #eee',
  },
  metricCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  metricLabel: {
    fontSize: '0.7rem',
    color: '#666',
    textTransform: 'uppercase',
    marginBottom: '0.25rem',
  },
  metricValue: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#333',
  },
  metricUnit: {
    fontSize: '0.7rem',
    color: '#888',
  },
  utilizationSection: {
    padding: '1rem',
    borderBottom: '1px solid #eee',
  },
  utilizationLabel: {
    fontSize: '0.75rem',
    color: '#666',
    marginBottom: '0.5rem',
  },
  utilizationBarContainer: {
    height: '8px',
    backgroundColor: '#e9ecef',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  utilizationBar: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.3s ease',
  },
  utilizationDetails: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.7rem',
    color: '#888',
    marginTop: '0.25rem',
  },
  costSection: {
    padding: '1rem',
    borderBottom: '1px solid #eee',
  },
  sectionTitle: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#333',
    textTransform: 'uppercase',
    marginBottom: '0.5rem',
  },
  costRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.85rem',
    color: '#666',
    padding: '0.25rem 0',
  },
  formatSection: {
    padding: '1rem',
  },
  formatList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  formatRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.8rem',
    padding: '0.25rem 0',
  },
  formatName: {
    color: '#333',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '70%',
  },
  formatUtilization: {
    fontWeight: 500,
  },
  moreFormats: {
    fontSize: '0.75rem',
    color: '#888',
    fontStyle: 'italic',
    paddingTop: '0.25rem',
  },
};

export default ComparisonColumn;
